"use client";

import { FC, useEffect } from "react";
import Breadcrumb from "./Breadcrumb";
import CompanyLogosMarquee from "./CompanyLogosMarquee";
import Footer from "./Footer";
import Header from "./Header";
import AnswerBlock from "./landing/AnswerBlock";
import BeforeAfter from "./landing/BeforeAfter";
import ComparisonCallout from "./landing/ComparisonCallout";
import FAQAccordion from "./landing/FAQAccordion";
import FeatureGrid from "./landing/FeatureGrid";
import FeatureSection from "./landing/FeatureSection";
import FinalCTA from "./landing/FinalCTA";
import Hero from "./landing/Hero";
import HowItWorks3Step from "./landing/HowItWorks3Step";
import PersonaGrid from "./landing/PersonaGrid";
import ReasonsMiniGrid from "./landing/ReasonsMiniGrid";
import RelatedTools from "./landing/RelatedTools";
import RivalComparison from "./landing/RivalComparison";
import TestimonialBlock from "./landing/TestimonialBlock";
import UseCaseStrip from "./landing/UseCaseStrip";
import type {
  ToolPageDataV2,
  V2Section,
} from "../app/(marketing)/tools/_data/v2/_types";

type ToolPageV2Props = {
  data: ToolPageDataV2;
};

// Single source of truth for v2 page composition. Walks `sections` and
// dispatches each entry to the corresponding component, so each per-
// page data file declares its own structure (pages 12 + 17 deviate
// from the canonical order). FAQ + Related + FinalCTA are emitted
// outside the loop because the spec mandates them on every page in
// fixed positions.
const renderSection = (section: V2Section, index: number) => {
  switch (section.type) {
    case "feature":
      return (
        <FeatureSection
          key={`feature-${index}`}
          h2={section.h2}
          body={section.body}
          ctaLabel={section.ctaLabel}
          ctaHref={section.ctaHref}
          videoSrc={section.videoSrc}
          videoAlt={section.videoAlt}
          imageSide={section.imageSide}
        />
      );
    case "howto":
      return (
        <HowItWorks3Step
          key={`howto-${index}`}
          sectionTitle={section.sectionTitle}
          steps={section.steps}
        />
      );
    case "personaGrid":
      return (
        <PersonaGrid
          key={`persona-${index}`}
          sectionTitle={section.sectionTitle}
          intro={section.intro}
          personas={section.personas}
        />
      );
    case "featureGrid":
      return (
        <FeatureGrid
          key={`grid-${index}`}
          sectionTitle={section.sectionTitle}
          description={section.description}
          features={section.features}
          columns={section.columns}
        />
      );
    case "reasonsMiniGrid":
      return (
        <ReasonsMiniGrid
          key={`reasons-${index}`}
          sectionTitle={section.sectionTitle}
          features={section.features}
        />
      );
    case "comparisonCallout":
      return <ComparisonCallout key={`callout-${index}`} body={section.body} />;
    case "testimonial":
      return (
        <TestimonialBlock
          key={`testimonial-${index}`}
          quote={section.quote}
          name={section.name}
          role={section.role}
          avatarSrc={section.avatarSrc}
        />
      );
    case "useCaseStrip":
      return (
        <UseCaseStrip
          key={`usecase-${index}`}
          sectionTitle={section.sectionTitle}
          intro={section.intro}
          items={section.items}
        />
      );
    case "answer":
      return (
        <AnswerBlock
          key={`answer-${index}`}
          heading={section.heading}
          body={section.body}
          specs={section.specs}
          updated={section.updated}
        />
      );
    case "beforeAfter":
      return (
        <BeforeAfter
          key={`before-after-${index}`}
          sectionTitle={section.sectionTitle}
          body={section.body}
          videoSrc={section.videoSrc}
          videoAlt={section.videoAlt}
        />
      );
    case "rivalComparison":
      return (
        <RivalComparison
          key={`rivals-${index}`}
          sectionTitle={section.sectionTitle}
          intro={section.intro}
          rivals={section.rivals}
          rows={section.rows}
          sources={section.sources}
        />
      );
    case "usedByLogos":
      return (
        <div
          key={`logos-${index}`}
          className="max-w-[1500px] px-3 md:px-12 mx-auto my-24"
        >
          <CompanyLogosMarquee />
        </div>
      );
    default: {
      // Exhaustive switch — TS errors here mean a section type was
      // added to _types.ts but not wired into the renderer.
      const _exhaustive: never = section;
      void _exhaustive;
      return null;
    }
  }
};

const ToolPageV2: FC<ToolPageV2Props> = ({ data }) => {
  // Seed `?task=shorts` so the embedded VideoInput hands the right
  // preset to the upload modal — mirrors the legacy ToolPage behavior.
  // Every v2 page targets the shorts pipeline.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("task") === "shorts") return;
    url.searchParams.set("task", "shorts");
    window.history.replaceState(null, "", url.toString());
  }, []);

  return (
    <div>
      <Header />
      <Breadcrumb className="justify-center" items={data.breadcrumbItems} />
      <Hero
        h1={data.hero.h1}
        subhead={data.hero.subhead}
        videoSrc={data.hero.videoSrc}
        videoAlt={data.hero.videoAlt}
        videoInputPlaceholder={data.hero.videoInputPlaceholder}
        sourcePlatformBadges={data.hero.sourcePlatformBadges}
      />
      {data.sections.map((section, index) => renderSection(section, index))}
      <FAQAccordion
        sectionTitle={data.faq.sectionTitle}
        faqs={data.faq.items}
      />
      <RelatedTools
        sectionTitle={data.related.sectionTitle}
        tools={data.related.tools}
      />
      <FinalCTA
        h2={data.finalCTA.h2}
        subhead={data.finalCTA.subhead}
        videoInputPlaceholder={
          data.finalCTA.videoInputPlaceholder ??
          data.hero.videoInputPlaceholder
        }
      />
      <Footer />
    </div>
  );
};

export default ToolPageV2;
